import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY } from '../utils/constants';
import { readMusterWorkbook, parseMusterSheet } from '../utils/musterParse';
import { classifyRows, designationFills, missingLeaveType, leaveYearStart } from '../utils/payrollCalc';
import {
  AlertTriangle, CheckCircle2, FileSpreadsheet, Info, Loader2, ShieldAlert, Upload,
} from 'lucide-react';

// Muster (attendance) upload → per-employee flags → payslip generation.
//
// Order of gates, strictest first:
//   1. Every ACTIVE employee must have a leave type set — the calculation
//      refuses to run otherwise, listing who is missing (nothing is assumed).
//   2. The FILE can be refused whole (duplicate/missing date columns etc.) —
//      musterParse.js decides that.
//   2b. MONTH CROSS-CHECK (08-09-2026): the person uploading picks the month
//      first (required, no default), and the sheet's date columns must agree
//      with it. On disagreement NOTHING is analysed — no database read, no
//      Save — and the refusal names both months. Neither source is trusted
//      alone: a wrong month writes a whole payroll to the wrong period.
//      The mismatch is stated FIRST, ahead of any fault in the sheet itself
//      (03-10-2026): a September muster still carrying August's header row
//      is a mismatch, and "missing 31-08-2026" on its own pointed at the
//      wrong thing.
//   3. Per employee: blocker flags mean NO payslip for that person until the
//      sheet or the employee record is fixed; warnings show but never change
//      pay (the muster stays authoritative). Cash employees are calculated
//      so month totals are the full payroll cost, but get NO payslip by
//      design (status 'excluded'): their pay is stored on payroll_attendance
//      exactly like a blocked employee's, so there is no slip row to PDF or
//      send — structural, not gated (decision 08-09-2026).
// Generation writes payroll_attendance rows for EVERYONE in the file
// (including blocked and Cash) and payslip rows only for clean, non-Cash
// employees — payslips holds only people who actually receive a document.
// The upload PRODUCES the month as calculated (05-09-2026): slips are
// created/updated to the computed figures, identical rows are skipped, and
// stored slips for employees not in the calculated month are DELETED — all
// shown in the write plan (sent slips called out with old vs new figures)
// before approval, and every write count-verified against the database.
// Figures are computed once here and stored — nothing recomputes on read.
//
// One write goes to the EMPLOYEE record (02-10-2026): a blank designation is
// filled from the muster's Designation column. Blanks only — a record that
// has a designation is never changed by an upload (designationFills in
// payrollCalc.js decides; the update itself is also guarded on the column
// still being empty). The fills are listed in the write plan. It runs last
// and is not part of the month's money: if it fails the month is still saved
// and the report says the designations were not filled.

const card = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  padding: 20,
};

const inr = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const fmtMonth = (ym) => {
  if (!/^\d{4}-\d{2}$/.test(ym || '')) return ym || '';
  const [y, m] = ym.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
};

const label = (r) => `${r.name || r.musterEmployeeId} (${r.musterEmployeeId})`;

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

// ─── Write plan ───────────────────────────────────────────────────────────────
// The upload PRODUCES the month as calculated (decision 05-09-2026): slips are
// created/updated to the computed figures, rows identical to what is stored
// are skipped (so updated_at — and the changed-after-send warning — only ever
// move on a real change), and stored slips for employees who no longer get
// one (not in the calculated month, blocked, or Cash) are deleted. Sent slips are never touched silently: the
// plan below is shown, old vs new figures included, before the user approves.
// The SAME plan drives the preview and the write, and every write is count-
// verified against the database response — the number approved is the number
// that lands.
const slipValues = (r) => ({
  gross: r.employee.gross_monthly,
  basic: r.employee.basic,
  da: r.employee.da,
  residual_pay: r.employee.residual_pay,
  pf: r.pay.pf,
  esic: r.pay.esic,
  professional_tax: r.pay.professionalTax,
  days_paid: r.pay.daysPaid,
  days_unpaid: r.pay.daysUnpaid,
  days_not_employed: r.pay.daysNotEmployed,
  daily_wage: r.pay.dailyWage,
  lwp_deduction: r.pay.unpaidDeduction,
  gross_earned: r.pay.grossEarned,
  net_pay: r.pay.netPay,
});

const numEq = (a, b) => ((a === null || a === undefined) ? (b === null || b === undefined) : Number(a) === Number(b));
const sameSlip = (oldRow, vals) => Object.keys(vals).every((k) => numEq(oldRow[k], vals[k]));

function buildPlan(rows, existingSlips) {
  const okRows = rows.filter((r) => r.status === 'ok');
  const byEmp = new Map(existingSlips.map((s) => [s.employee_id, s]));
  const creates = [];
  const updates = []; // { r, old }
  const unchanged = [];
  for (const r of okRows) {
    const old = byEmp.get(r.employee.id);
    if (!old) creates.push(r);
    else if (sameSlip(old, slipValues(r))) unchanged.push({ r, old });
    else updates.push({ r, old });
  }
  const okIds = new Set(okRows.map((r) => r.employee.id));
  const deletes = existingSlips.filter((s) => !okIds.has(s.employee_id));
  return {
    creates,
    updates,
    unchanged,
    deletes,
    sentChanged: updates.filter((u) => u.old.sent_at),
    sentDeleted: deletes.filter((s) => s.sent_at),
  };
}
const statLabel = { fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' };
const statValue = { fontSize: '1.25rem', fontWeight: 700, color: NAVY, fontVariantNumeric: 'tabular-nums' };

export default function PayrollMusterUpload({ onOpenEmployees, onReviewMonth }) {
  const [employees, setEmployees] = useState(null); // null = loading
  const [empError, setEmpError] = useState('');

  // The month the uploader asserts, 'YYYY-MM' (the native month input's
  // value format, same as the parser's). Deliberately no default.
  const [pickedMonth, setPickedMonth] = useState('');
  const [fileName, setFileName] = useState('');
  const [book, setBook] = useState(null);
  const [sheetChoice, setSheetChoice] = useState('');
  const [parseError, setParseError] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  // data: { month, rows (classified), rowProblems, missingFromMuster, existingSlips }
  const [data, setData] = useState(null);

  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [report, setReport] = useState(null);
  const fileRef = useRef(null);

  const loadEmployees = useCallback(async () => {
    setEmpError('');
    setEmployees(null);
    const { data: emps, error } = await supabase
      .from('employees')
      .select('id, employee_code, name, department, designation, payment_type, leave_type, is_active, gross_monthly, basic, da, residual_pay, pf, esic, professional_tax')
      .order('employee_code');
    if (error) {
      setEmpError(/column|schema cache/i.test(error.message || '')
        ? `Could not load the employee master: the table is missing the new columns. Paste supabase/manual/2026-10-02_employees_designation_esic_number_ifsc.sql into the Supabase SQL editor, then retry. (${error.message})`
        : `Could not load the employee master: ${error.message}`);
    }
    else setEmployees(emps || []);
  }, []);
  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  const reset = () => {
    setBook(null);
    setSheetChoice('');
    setParseError('');
    setData(null);
    setGenError('');
    setFileName('');
  };

  // Parse the chosen sheet, cross-check its month against the picked month,
  // then join the database: YTD leave sums for the leave year BEFORE this
  // month (a re-upload must not count itself), and any payslips already
  // stored for the month. `monthSel` is passed in (not read from state) so a
  // month change can re-run the check on the loaded file immediately.
  const applySheet = async (b, name, monthSel) => {
    setSheetChoice(name);
    setData(null);
    setParseError('');
    setGenError('');
    setReport(null);
    if (!name) return;
    const parsed = parseMusterSheet(b.wb, name);
    // Month disagreement leads, even when the sheet is also faulty on its own
    // terms: the header row naming the wrong month is usually the whole story,
    // and the sheet's faults (missing days etc.) were judged against THAT
    // month, so they come second and are framed as such.
    if (parsed.month && monthSel && parsed.month !== monthSel) {
      setParseError(
        `Month mismatch — nothing was analysed. You selected ${fmtMonth(monthSel)}, but this sheet's date columns are headed ${fmtMonth(parsed.month)}. `
        + 'Pick the right month, or correct the sheet\'s date headers if the attendance really is for the month you selected; neither is trusted on its own.'
        + (parsed.error ? ` Read as ${fmtMonth(parsed.month)}, the sheet also fails: ${parsed.problems.join(' ')}` : ''),
      );
      return;
    }
    if (parsed.error) { setParseError(parsed.error); return; }
    if (!monthSel) {
      setParseError(`Choose the month first. The sheet's date columns are for ${fmtMonth(parsed.month)} — nothing is analysed until you confirm the month yourself.`);
      return;
    }
    setAnalyzing(true);
    try {
      const [ytdRes, slipsRes] = await Promise.all([
        supabase.from('payroll_attendance')
          .select('employee_id, cl_days, ml_days')
          .gte('month', leaveYearStart(parsed.month))
          .lt('month', parsed.month)
          .not('employee_id', 'is', null),
        supabase.from('payslips')
          .select('id, employee_id, gross, basic, da, residual_pay, pf, esic, professional_tax, days_paid, days_unpaid, days_not_employed, daily_wage, lwp_deduction, gross_earned, net_pay, sent_at, send_status, pdf_path')
          .eq('month', parsed.month),
      ]);
      if (ytdRes.error) throw ytdRes.error;
      if (slipsRes.error) throw slipsRes.error;
      const ytd = new Map();
      for (const a of ytdRes.data || []) {
        const y = ytd.get(a.employee_id) || { cl: 0, ml: 0 };
        y.cl += a.cl_days;
        y.ml += a.ml_days;
        ytd.set(a.employee_id, y);
      }
      const { rows, missingFromMuster } = classifyRows({ rows: parsed.rows, employees, ytd });
      setData({
        month: parsed.month,
        rows,
        rowProblems: parsed.rowProblems,
        missingFromMuster,
        existingSlips: slipsRes.data || [],
      });
    } catch (err) {
      setParseError(`Could not analyse the sheet against the database: ${err.message || err}`);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!pickedMonth) { setParseError('Choose the month before uploading the muster.'); if (fileRef.current) fileRef.current.value = ''; return; }
    reset();
    setReport(null);
    setFileName(file.name);
    try {
      const buffer = await file.arrayBuffer();
      const b = readMusterWorkbook(buffer);
      setBook(b);
      const complete = b.sheets.filter((s) => s.complete);
      const matching = complete.filter((s) => s.month === pickedMonth);
      // Preselect only when exactly one sheet is usable AND for the picked
      // month. A lone usable sheet for a DIFFERENT month is applied so the
      // mismatch refusal is shown in full rather than a vague "choose"; so is
      // the only sheet of a one-sheet file, whatever is wrong with it — there
      // is nothing to choose, and its refusal should be read at once.
      if (matching.length === 1) await applySheet(b, matching[0].name, pickedMonth);
      else if (complete.length === 1) await applySheet(b, complete[0].name, pickedMonth);
      else if (b.sheets.length === 1) await applySheet(b, b.sheets[0].name, pickedMonth);
      else if (complete.length === 0) setParseError('No sheet in this file is a usable muster report. Pick a sheet below to see exactly what is wrong with it.');
      // multiple usable sheets: staff must choose — no guessing
    } catch (err) {
      setParseError(`Could not read the file: ${err.message || err}`);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  // Writes: replace the month's attendance wholesale (it is fully re-derivable
  // from the file — on failure the fix is always "re-upload"), then upsert
  // payslips for clean employees only. sent_at / send_status / pdf_path are
  // never touched; the Review tab's changed-after-send warning covers updates.
  const handleMonthChange = (value) => {
    setPickedMonth(value);
    setReport(null);
    // A loaded file is re-checked against the new month at once — switching
    // to the right month recovers from a mismatch without re-uploading.
    if (book && sheetChoice) applySheet(book, sheetChoice, value);
    else if (book) { setData(null); setParseError(''); }
  };

  const handleGenerate = async () => {
    if (!data || generating) return;
    setGenerating(true);
    setGenError('');
    try {
      const { rows, month, existingSlips } = data;
      // Last line of defence: the analysed month must still be the one the
      // uploader asserted. Cannot differ after applySheet's check, but this
      // write goes to a whole period — it is verified, not assumed.
      if (month !== pickedMonth) throw new Error(`Month mismatch at save time (sheet ${fmtMonth(month)}, selected ${fmtMonth(pickedMonth)}). Nothing was written.`);

      const { error: delErr } = await supabase.from('payroll_attendance').delete().eq('month', month);
      if (delErr) throw delErr;

      const { data: inserted, error: insErr } = await supabase.from('payroll_attendance').insert(
        rows.map((r) => ({
          month,
          muster_employee_id: r.musterEmployeeId,
          muster_name: r.name,
          employee_id: r.employee?.id ?? null,
          fd_days: r.counts.fd,
          l1_days: r.counts.l1,
          cl_days: r.counts.cl,
          ml_days: r.counts.ml,
          wo_days: r.counts.wo,
          lwp_days: r.counts.lwp,
          absent_days: r.counts.absent,
          dash_days: r.counts.dash,
          unknown_cells: r.unknownCells,
          stated_summary: r.statedSummary,
          flags: r.flags,
          status: r.status,
          // Blocked (05-09-2026) and Cash / excluded (08-09-2026) employees
          // get no payslip row, but their computed pay still feeds the month
          // total — stored HERE, where the PDF/send machinery can never see
          // it. Ok rows keep NULLs: their money lives on the payslip (one
          // money record per person per month). Blocked-without-pay (not in
          // DB / no salary) stays NULL and is reported as missing from the
          // total.
          ...(r.status !== 'ok' && r.pay ? {
            days_paid: r.pay.daysPaid,
            days_unpaid: r.pay.daysUnpaid,
            daily_wage: r.pay.dailyWage,
            unpaid_deduction: r.pay.unpaidDeduction,
            gross: r.employee.gross_monthly,
            pf: r.pay.pf,
            esic: r.pay.esic,
            professional_tax: r.pay.professionalTax,
            gross_earned: r.pay.grossEarned,
            net_pay: r.pay.netPay,
          } : {}),
        })),
      ).select('id, muster_employee_id');
      if (insErr) throw insErr;
      const attIdByCode = new Map((inserted || []).map((a) => [a.muster_employee_id, a.id]));

      // The SAME plan the preview showed — approve what you saw, get what you
      // approved. Every write is verified against the database's response.
      const plan = buildPlan(rows, existingSlips);
      const nowIso = new Date().toISOString();
      const toWrite = [...plan.creates, ...plan.updates.map((u) => u.r)];

      if (toWrite.length) {
        const payload = toWrite.map((r) => ({
          employee_id: r.employee.id,
          month,
          // Salary snapshot + computed figures — stored once, never
          // recomputed on read; a later salary revision never changes them.
          ...slipValues(r),
          attendance_id: attIdByCode.get(r.musterEmployeeId) ?? null,
          updated_at: nowIso,
        }));
        const wrote = await supabase.from('payslips')
          .upsert(payload, { onConflict: 'employee_id,month' })
          .select('employee_id');
        if (wrote.error) throw wrote.error;
        if ((wrote.data || []).length !== payload.length) {
          throw new Error(`Write verification FAILED: planned ${payload.length} slip writes, the database confirmed ${(wrote.data || []).length}. Nothing else was touched — investigate before re-running.`);
        }
      }

      let deletedPdfNote = '';
      if (plan.deletes.length) {
        const del = await supabase.from('payslips')
          .delete()
          .in('id', plan.deletes.map((s) => s.id))
          .select('id');
        if (del.error) throw del.error;
        if ((del.data || []).length !== plan.deletes.length) {
          throw new Error(`Delete verification FAILED: planned ${plan.deletes.length} stale-slip deletions, the database confirmed ${(del.data || []).length}. Investigate before re-running.`);
        }
        const paths = plan.deletes.map((s) => s.pdf_path).filter(Boolean);
        if (paths.length) {
          const rm = await supabase.storage.from('payslips').remove(paths);
          if (rm.error) deletedPdfNote = `Stored PDF cleanup failed for ${paths.length} deleted slip(s) (${rm.error.message}) — harmless orphan files in the payslips bucket.`;
        }
      }

      // Blank designations filled from the muster — the list the write plan
      // showed. Last, and outside the month's money: a failure here is
      // reported, it does not undo or cast doubt on the payroll just saved.
      // One update per distinct designation, each guarded on the column
      // still being empty so a value typed in meanwhile is never overwritten.
      const fills = designationFills(rows);
      const filled = [];
      let fillNote = '';
      if (fills.length) {
        const byValue = new Map();
        for (const f of fills) byValue.set(f.designation, [...(byValue.get(f.designation) || []), f]);
        for (const [designation, group] of byValue) {
          const res = await supabase.from('employees')
            .update({ designation })
            .in('id', group.map((f) => f.employeeId))
            .is('designation', null)
            .select('id');
          if (res.error) { fillNote = `Designations could not be filled from the muster (${res.error.message}). The payroll above is saved; re-upload the same file to try again, or type them in the Employees tab.`; break; }
          const done = new Set((res.data || []).map((e) => e.id));
          filled.push(...group.filter((f) => done.has(f.employeeId)));
        }
        if (!fillNote && filled.length !== fills.length) {
          fillNote = `${fills.length - filled.length} of ${fills.length} planned designation fills were skipped — those records were no longer blank when saving.`;
        }
        if (filled.length) {
          const byId = new Map(filled.map((f) => [f.employeeId, f.designation]));
          setEmployees((prev) => (prev || []).map((e) => (byId.has(e.id) ? { ...e, designation: byId.get(e.id) } : e)));
        }
      }

      const nameById = new Map(employees.map((e) => [e.id, `${e.name} (${e.employee_code})`]));
      const empLabel = (s) => nameById.get(s.employee_id) || s.employee_id;
      setReport({
        month,
        attendance: rows.length,
        created: plan.creates.length,
        updated: plan.updates.length,
        unchanged: plan.unchanged.length,
        deleted: plan.deletes.map(empLabel),
        sentChanged: plan.sentChanged.map((u) => label(u.r)),
        sentDeleted: plan.sentDeleted.map(empLabel),
        deletedPdfNote,
        designationsFilled: filled.map((f) => `${f.name} (${f.code}): ${f.designation}`),
        designationNote: fillNote,
        blocked: rows.filter((r) => r.status === 'blocked').map(label),
        blockedNet: rows.filter((r) => r.status === 'blocked' && r.pay).reduce((a, r) => a + r.pay.netPay, 0),
        blockedNoPay: rows.filter((r) => r.status === 'blocked' && !r.pay).map(label),
        cash: rows.filter((r) => r.status === 'excluded').map(label),
        cashNet: rows.filter((r) => r.status === 'excluded').reduce((a, r) => a + r.pay.netPay, 0),
        warnings: rows.filter((r) => r.flags.some((f) => f.level === 'warning')).length,
      });
      reset();
    } catch (err) {
      setGenError(`Generation failed — nothing may be partially trusted; fix the cause and re-upload the file. ${err.message || err}`);
    } finally {
      setGenerating(false);
    }
  };

  // ── Employee-master gates ───────────────────────────────────────────────────
  if (empError) {
    return (
      <div style={{ ...card, maxWidth: 520 }}>
        <p style={{ fontSize: '0.83rem', color: 'var(--color-danger)', marginBottom: 12 }}>{empError}</p>
        <button type="button" className="btn btn-outline btn-sm" onClick={loadEmployees}>Retry</button>
      </div>
    );
  }
  if (employees === null) {
    return (
      <div style={{ ...card, maxWidth: 420, textAlign: 'center', color: 'var(--color-ink-muted)', fontSize: '0.85rem' }}>
        <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} aria-hidden="true" /> Loading the employee master…
      </div>
    );
  }

  const noLeaveType = missingLeaveType(employees);
  if (noLeaveType.length) {
    return (
      <div style={{ ...card, maxWidth: 640, borderColor: 'var(--color-warning-border)' }}>
        <p style={{ fontSize: '0.9rem', fontWeight: 700, color: NAVY, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <ShieldAlert size={17} style={{ color: 'var(--color-warning-strong)' }} aria-hidden="true" />
          Attendance cannot be processed yet
        </p>
        <p style={{ fontSize: '0.83rem', color: 'var(--color-ink-secondary)', lineHeight: 1.55, marginBottom: 10 }}>
          {noLeaveType.length} active employee{noLeaveType.length === 1 ? ' has' : 's have'} no leave type set. The calculation refuses
          to run until every active employee is marked L1 (4 paid L1 days per month) or WO (weekly offs, no L1) — nothing is assumed.
        </p>
        <p style={{ fontSize: '0.8rem', color: 'var(--color-ink)', lineHeight: 1.7, marginBottom: 14 }}>
          {noLeaveType.map((e) => `${e.name} (${e.employee_code})`).join('; ')}
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" className="btn btn-navy btn-sm" onClick={onOpenEmployees}>Open the Employees tab</button>
          <button type="button" className="btn btn-outline btn-sm" onClick={loadEmployees}>Re-check</button>
        </div>
      </div>
    );
  }

  // ── Derived view state ──────────────────────────────────────────────────────
  const okRows = data ? data.rows.filter((r) => r.status === 'ok') : [];
  const blockedRows = data ? data.rows.filter((r) => r.status === 'blocked') : [];
  // Cash employees: calculated, stored on attendance, no slip by design.
  // A Cash employee with a blocker is in blockedRows instead (data problem
  // first) — the two lists never overlap.
  const cashRows = data ? data.rows.filter((r) => r.status === 'excluded') : [];
  // The computed-figures table is the COMPLETE list of what the month pays:
  // slip-getting employees AND the no-slip ones (blocked + Cash). Its total
  // matches the month total. Blocked rows with no calculable pay cannot
  // appear (nothing to show) — the blockers card calls them out as missing.
  const payRows = data ? data.rows.filter((r) => r.pay) : [];
  const payRowsBlocked = payRows.filter((r) => r.status === 'blocked');
  const payRowsCash = payRows.filter((r) => r.status === 'excluded');
  const warningRows = data ? data.rows.filter((r) => r.flags.some((f) => f.level === 'warning')) : [];
  const hasDuplicates = data ? data.rows.some((r) => r.flags.some((f) => f.code === 'duplicate_muster_row')) : false;
  // Rows that cannot even be stored make the whole upload unsafe: an ID-less
  // row would vanish, a duplicated ID cannot satisfy the month+ID uniqueness.
  const fileBlocked = data ? (data.rowProblems.length > 0 || hasDuplicates) : false;
  // One plan object drives the preview AND the write — see buildPlan.
  const plan = data ? buildPlan(data.rows, data.existingSlips) : null;
  const fillPlan = data ? designationFills(data.rows) : [];
  const nameByEmpId = new Map((employees || []).map((e) => [e.id, `${e.name} (${e.employee_code})`]));
  const sentImpact = plan ? plan.sentChanged.length + plan.sentDeleted.length : 0;

  return (
    <div>
      {/* ── File + sheet picker ─────────────────────────────────────────────── */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label htmlFor="muster-month" style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-ink-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Month
            </label>
            <input
              id="muster-month"
              type="month"
              value={pickedMonth}
              onChange={(e) => handleMonthChange(e.target.value)}
              disabled={generating}
              style={{ background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '7px 10px', color: NAVY, fontSize: '0.84rem', fontWeight: 600, outline: 'none' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-ink-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Muster report (.xlsx)
            </label>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => handleFile(e.target.files?.[0])}
              disabled={!pickedMonth || generating}
              title={pickedMonth ? '' : 'Choose the month first'}
              style={{ fontSize: '0.82rem' }}
            />
          </div>
          {fileName && (
            <span style={{ fontSize: '0.78rem', color: 'var(--color-ink-secondary)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <FileSpreadsheet size={14} aria-hidden="true" /> {fileName}
            </span>
          )}
          {data && (
            <span style={{ fontSize: '0.84rem', fontWeight: 700, color: NAVY, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <CheckCircle2 size={15} style={{ color: 'var(--color-success-strong)' }} aria-hidden="true" />
              {fmtMonth(data.month)} — the sheet's dates match your selection
            </span>
          )}
        </div>
        {book && (
          <div style={{ marginTop: 14, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-ink-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Sheet to parse
              </label>
              <select
                value={sheetChoice}
                onChange={(e) => applySheet(book, e.target.value, pickedMonth)}
                style={{ background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '8px 10px', color: NAVY, fontSize: '0.84rem', fontWeight: 600, outline: 'none', maxWidth: 380 }}
              >
                <option value="">Choose a sheet…</option>
                {book.sheets.map((s) => (
                  <option key={s.name} value={s.name}>
                    {s.name} — {s.month && s.month !== pickedMonth
                      ? `dates are headed ${fmtMonth(s.month)}, not ${fmtMonth(pickedMonth)}`
                      : !s.complete
                        ? 'not usable (choose to see why)'
                        : `${fmtMonth(s.month)}, ${s.employeeRows} employees`}
                  </option>
                ))}
              </select>
            </div>
            <button type="button" className="btn btn-outline btn-sm" onClick={reset} style={{ marginBottom: 2 }}>
              Cancel
            </button>
          </div>
        )}
        <p style={{ fontSize: '0.75rem', color: 'var(--color-ink-faint)', marginTop: 10, marginBottom: 0 }}>
          {pickedMonth ? `You selected ${fmtMonth(pickedMonth)}. ` : 'Choose the month first. '}
          The sheet's date columns must agree with it — on any disagreement the file is refused, not corrected. Salary components come from the employee master, not this file.
        </p>
      </div>

      {analyzing && (
        <div style={{ ...card, marginBottom: 16, color: 'var(--color-ink-muted)', fontSize: '0.85rem' }}>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', verticalAlign: -3, marginRight: 8 }} aria-hidden="true" />
          Checking the sheet against the employee database…
        </div>
      )}

      {parseError && (
        <div style={{ ...card, marginBottom: 16, borderColor: 'var(--color-danger-border)', background: 'var(--color-danger-bg)' }}>
          <p style={{ fontSize: '0.83rem', color: 'var(--color-danger-strong)', margin: 0, display: 'flex', gap: 8, lineHeight: 1.5 }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true" /> {parseError}
          </p>
        </div>
      )}

      {data && (
        <>
          {/* ── Summary tiles ──────────────────────────────────────────────── */}
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
              <div><div style={statValue}>{data.rows.length}</div><div style={statLabel}>In the muster</div></div>
              <div><div style={statValue}>{okRows.length}</div><div style={statLabel}>Payslips ready</div></div>
              <div><div style={{ ...statValue, color: blockedRows.length ? 'var(--color-danger)' : NAVY }}>{blockedRows.length}</div><div style={statLabel}>Blocked — no slip</div></div>
              <div><div style={{ ...statValue, color: warningRows.length ? 'var(--color-warning-strong)' : NAVY }}>{warningRows.length}</div><div style={statLabel}>With warnings</div></div>
              <div><div style={statValue}>{cashRows.length}</div><div style={statLabel}>Cash — no slip</div></div>
              <div><div style={statValue}>{data.missingFromMuster.length}</div><div style={statLabel}>Not in muster</div></div>
            </div>
          </div>

          {/* ── File-level problems: generation disabled entirely ──────────── */}
          {fileBlocked && (
            <div style={{ ...card, marginBottom: 16, borderColor: 'var(--color-danger-border)', background: 'var(--color-danger-bg)' }}>
              <p style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--color-danger-strong)', display: 'flex', gap: 8, marginBottom: 8 }}>
                <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
                This file cannot be saved at all — fix the sheet and re-upload
              </p>
              <ul style={{ fontSize: '0.8rem', color: 'var(--color-ink)', lineHeight: 1.6, margin: 0, paddingLeft: 18 }}>
                {data.rowProblems.map((p, i) => (
                  <li key={`rp-${i}`}><strong>{p.name}</strong> (row {p.row}): {p.message}</li>
                ))}
                {hasDuplicates && (
                  <li>Duplicate Employee IDs: {[...new Set(data.rows.filter((r) => r.flags.some((f) => f.code === 'duplicate_muster_row')).map((r) => r.musterEmployeeId))].join(', ')} — two rows for one person cannot both be stored.</li>
                )}
              </ul>
            </div>
          )}

          {/* ── Blockers ───────────────────────────────────────────────────── */}
          {blockedRows.length > 0 && (
            <div style={{ ...card, marginBottom: 16, borderColor: 'var(--color-danger-border)' }}>
              <p style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--color-danger-strong)', marginBottom: 8 }}>
                No payslip for {blockedRows.length} employee{blockedRows.length === 1 ? '' : 's'} until resolved
              </p>
              {blockedRows.map((r) => (
                <div key={r.musterEmployeeId} style={{ marginBottom: 8 }}>
                  <p style={{ fontSize: '0.82rem', fontWeight: 700, color: NAVY, margin: '0 0 2px' }}>{label(r)}</p>
                  {r.flags.filter((f) => f.level === 'blocker').map((f, i) => (
                    <p key={i} style={{ fontSize: '0.78rem', color: 'var(--color-ink-secondary)', margin: '0 0 2px 12px', lineHeight: 1.5 }}>{f.message}</p>
                  ))}
                  {r.pay ? (
                    <p style={{ fontSize: '0.78rem', color: NAVY, fontWeight: 600, margin: '2px 0 2px 12px', fontVariantNumeric: 'tabular-nums' }}>
                      Calculated for the month total (from the day cells): {r.pay.daysPaid} paid / {r.pay.daysUnpaid} unpaid / {r.pay.daysNotEmployed} not employed,
                      deduction {inr(r.pay.unpaidDeduction)}, net {inr(r.pay.netPay)}. No slip, no send.
                    </p>
                  ) : (
                    <p style={{ fontSize: '0.78rem', color: 'var(--color-warning-strong)', fontWeight: 600, margin: '2px 0 2px 12px' }}>
                      Pay cannot be calculated (no employee record / no salary) — MISSING from the month total until fixed.
                    </p>
                  )}
                </div>
              ))}
              <p style={{ fontSize: '0.75rem', color: 'var(--color-ink-faint)', margin: '10px 0 0' }}>
                Attendance and the calculated figures are still saved so the month total is complete; only the payslip is withheld.
                Fix the muster or the employee record, then re-upload.
              </p>
            </div>
          )}

          {/* ── Warnings ───────────────────────────────────────────────────── */}
          {warningRows.length > 0 && (
            <div style={{ ...card, marginBottom: 16, borderColor: 'var(--color-warning-border)', background: 'var(--color-warning-bg)' }}>
              <p style={{ fontSize: '0.85rem', fontWeight: 700, color: NAVY, marginBottom: 8 }}>
                Warnings — pay follows the muster as uploaded; correct the sheet first if any of this should change pay
              </p>
              {warningRows.map((r) => (
                <div key={r.musterEmployeeId} style={{ marginBottom: 8 }}>
                  <p style={{ fontSize: '0.82rem', fontWeight: 700, color: NAVY, margin: '0 0 2px' }}>{label(r)}</p>
                  {r.flags.filter((f) => f.level === 'warning').map((f, i) => (
                    <p key={i} style={{ fontSize: '0.78rem', color: 'var(--color-ink-secondary)', margin: '0 0 2px 12px', lineHeight: 1.5 }}>{f.message}</p>
                  ))}
                </div>
              ))}
              <p style={{ fontSize: '0.75rem', color: 'var(--color-ink-faint)', margin: '10px 0 0' }}>
                Leave totals are summed from stored attendance from {leaveYearStart(data.month)} onward — months uploaded before this system cannot be seen.
              </p>
            </div>
          )}

          {/* ── Info ───────────────────────────────────────────────────────── */}
          {(data.missingFromMuster.length > 0 || cashRows.length > 0) && (
            <div style={{ ...card, marginBottom: 16 }}>
              <p style={{ fontSize: '0.85rem', fontWeight: 700, color: NAVY, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Info size={15} aria-hidden="true" /> For information — not errors
              </p>
              <ul style={{ fontSize: '0.8rem', color: 'var(--color-ink-secondary)', lineHeight: 1.7, margin: 0, paddingLeft: 18 }}>
                {data.missingFromMuster.length > 0 && (
                  <li>
                    Active in the employee database but not in this muster (no attendance, no slip — expected for staff whose
                    attendance is not tracked; deactivate genuine leavers in the Employees tab):{' '}
                    {data.missingFromMuster.map((e) => `${e.name} (${e.employee_code})`).join('; ')}.
                  </li>
                )}
                {cashRows.length > 0 && (
                  <li>
                    Payment type Cash — calculated for the month total (net {inr(cashRows.reduce((a, r) => a + r.pay.netPay, 0))}) and stored on attendance,
                    no payslip by design (no slip row, no PDF, nothing to send): {cashRows.map(label).join('; ')}.
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* ── Write plan — exactly what pressing Save will do ─────────────── */}
          {plan && (
            <div style={{ ...card, marginBottom: 16, borderColor: (plan.sentChanged.length || plan.sentDeleted.length) ? 'var(--color-warning-border)' : 'var(--color-border)' }}>
              <p style={{ fontSize: '0.85rem', fontWeight: 700, color: NAVY, marginBottom: 8 }}>
                Write plan for {fmtMonth(data.month)} — this is exactly what saving will do
              </p>
              <ul style={{ fontSize: '0.8rem', color: 'var(--color-ink-secondary)', lineHeight: 1.7, margin: 0, paddingLeft: 18 }}>
                <li><strong>{plan.creates.length}</strong> new payslip{plan.creates.length === 1 ? '' : 's'} created.</li>
                <li><strong>{plan.updates.length}</strong> updated to the calculated figures (send state and PDFs kept; changed-after-send is flagged on Review).</li>
                <li><strong>{plan.unchanged.length}</strong> already identical — not rewritten (so no false "changed after send" flags).</li>
                <li>
                  <strong>{plan.deletes.length}</strong> stored slip{plan.deletes.length === 1 ? '' : 's'} for employees who no longer get one (not in the calculated month, blocked, or Cash) deleted
                  {plan.deletes.length ? ` — ${plan.deletes.map((s) => nameByEmpId.get(s.employee_id) || s.employee_id).join('; ')}` : ''}.
                </li>
                {fillPlan.length > 0 && (
                  <li>
                    <strong>{fillPlan.length}</strong> employee record{fillPlan.length === 1 ? '' : 's'} with no designation filled in from this muster
                    {' '}— {fillPlan.map((f) => `${f.name} (${f.code}): ${f.designation}`).join('; ')}.
                    {' '}Records that already have a designation are never changed.
                  </li>
                )}
              </ul>
              {plan.sentChanged.length > 0 && (
                <div style={{ marginTop: 10, background: 'var(--color-warning-bg)', border: '1px solid var(--color-warning-border)', borderRadius: 6, padding: '10px 12px' }}>
                  <p style={{ fontSize: '0.8rem', fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>
                    {plan.sentChanged.length} slip{plan.sentChanged.length === 1 ? ' has' : 's have'} ALREADY BEEN SENT and will change — the employee holds the old version. Your call to proceed:
                  </p>
                  {plan.sentChanged.map((u) => (
                    <p key={u.r.musterEmployeeId} style={{ fontSize: '0.78rem', color: 'var(--color-ink)', margin: '2px 0', lineHeight: 1.6, fontVariantNumeric: 'tabular-nums' }}>
                      <strong>{label(u.r)}</strong> (sent {fmtDate(u.old.sent_at)}): net {inr(u.old.net_pay)} → <strong>{inr(u.r.pay.netPay)}</strong>
                      {' · '}days {u.old.days_paid ?? '—'}/{u.old.days_unpaid ?? '—'} → {u.r.pay.daysPaid}/{u.r.pay.daysUnpaid}
                      {' · '}deduction {inr(u.old.lwp_deduction)} → {inr(u.r.pay.unpaidDeduction)}
                      {' · '}gross earned {inr(u.old.gross_earned)} → {inr(u.r.pay.grossEarned)}
                    </p>
                  ))}
                  <p style={{ fontSize: '0.75rem', color: 'var(--color-ink-muted)', margin: '6px 0 0' }}>
                    After saving: regenerate their PDFs and re-send from the Review tab.
                  </p>
                </div>
              )}
              {plan.sentDeleted.length > 0 && (
                <div style={{ marginTop: 10, background: 'var(--color-warning-bg)', border: '1px solid var(--color-warning-border)', borderRadius: 6, padding: '10px 12px' }}>
                  <p style={{ fontSize: '0.8rem', fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>
                    {plan.sentDeleted.length} SENT slip{plan.sentDeleted.length === 1 ? '' : 's'} will be DELETED — the employee keeps what was sent, our record of it goes:
                  </p>
                  {plan.sentDeleted.map((s) => (
                    <p key={s.id} style={{ fontSize: '0.78rem', color: 'var(--color-ink)', margin: '2px 0', fontVariantNumeric: 'tabular-nums' }}>
                      <strong>{nameByEmpId.get(s.employee_id) || s.employee_id}</strong> (sent {fmtDate(s.sent_at)}): stored net {inr(s.net_pay)}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {genError && (
            <div style={{ ...card, marginBottom: 16, borderColor: 'var(--color-danger-border)', background: 'var(--color-danger-bg)' }}>
              <p style={{ fontSize: '0.83rem', color: 'var(--color-danger-strong)', margin: 0 }}>{genError}</p>
            </div>
          )}

          {/* ── Generate ───────────────────────────────────────────────────── */}
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button className="btn btn-gold" onClick={handleGenerate} disabled={generating || fileBlocked}>
                {generating
                  ? 'Saving…'
                  : (<><Upload size={15} style={{ marginRight: 6, verticalAlign: -2 }} aria-hidden="true" />
                      Save attendance for {data.rows.length} &amp; write payslips ({plan ? `${plan.creates.length} new, ${plan.updates.length} updated, ${plan.deletes.length} deleted` : okRows.length}) — {fmtMonth(data.month)}
                      {sentImpact > 0 ? ` — affects ${sentImpact} SENT slip${sentImpact === 1 ? '' : 's'}` : ''}</>)}
              </button>
              <button type="button" className="btn btn-outline" onClick={reset} disabled={generating}>
                Cancel — discard this parse
              </button>
              <span style={{ fontSize: '0.75rem', color: 'var(--color-ink-faint)' }}>
                {fileBlocked
                  ? 'Disabled while the file has rows that cannot be stored (see above).'
                  : 'Nothing is written until you save. Figures below are stored as-is on each slip.'}
              </span>
            </div>
          </div>

          {/* ── Computed figures preview — the complete list of what the month
               pays: slips AND blocked (no-slip) employees. Total matches the
               month total. ─────────────────────────────────────────────────── */}
          {payRows.length > 0 && (
            <div style={{ ...card, padding: 0, overflowX: 'auto', marginBottom: 16 }}>
              <table className="data-table" style={{ width: '100%', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Code</th>
                    <th style={{ textAlign: 'left' }}>Name</th>
                    <th style={{ textAlign: 'left' }}>Slip</th>
                    <th style={{ textAlign: 'right' }}>Days Paid</th>
                    <th style={{ textAlign: 'right' }}>Days Unpaid</th>
                    <th style={{ textAlign: 'right' }} title="Not employed on those dates ('-' in the muster)">Not Emp.</th>
                    <th style={{ textAlign: 'right' }}>Daily Wage</th>
                    <th style={{ textAlign: 'right' }} title="(days unpaid + days not employed) x daily wage">Unpaid Deduction</th>
                    <th style={{ textAlign: 'right' }}>Gross Earned</th>
                    <th style={{ textAlign: 'right' }}>PF</th>
                    <th style={{ textAlign: 'right' }}>ESIC</th>
                    <th style={{ textAlign: 'right' }}>Prof. Tax</th>
                    <th style={{ textAlign: 'right' }}>Net Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {payRows.map((r) => (
                    <tr key={r.musterEmployeeId} style={r.status === 'blocked' ? { background: 'var(--color-warning-bg)' } : undefined}>
                      <td style={{ fontVariantNumeric: 'tabular-nums' }}>{r.musterEmployeeId}</td>
                      <td>{r.name}</td>
                      <td>
                        {r.status === 'blocked'
                          ? <span className="badge badge-amber" title="Blocked — no slip until the muster or employee record is fixed">Blocked — no slip</span>
                          : r.status === 'excluded'
                            ? <span className="badge badge-grey" title="Payment type Cash — no payslip by design; pay stored on attendance">Cash — no slip</span>
                            : <span style={{ color: 'var(--color-ink-faint)' }}>Yes</span>}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.pay.daysPaid}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.pay.daysUnpaid ? 'var(--color-danger)' : undefined }}>{r.pay.daysUnpaid}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.pay.daysNotEmployed ? 'var(--color-danger)' : undefined }}>{r.pay.daysNotEmployed}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(r.pay.dailyWage)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(r.pay.unpaidDeduction)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(r.pay.grossEarned)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(r.pay.pf)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(r.pay.esic)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(r.pay.professionalTax)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{inr(r.pay.netPay)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--color-border-strong)', fontWeight: 700 }}>
                    <td colSpan={6} style={{ color: NAVY }}>
                      Month total — {payRows.length} employees ({okRows.length} slips, {payRowsBlocked.length} blocked, {payRowsCash.length} Cash — no slip)
                      {blockedRows.some((r) => !r.pay) ? '; incomplete — see blocked employees without figures above' : ''}
                    </td>
                    <td />
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(payRows.reduce((a, r) => a + r.pay.unpaidDeduction, 0))}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(payRows.reduce((a, r) => a + r.pay.grossEarned, 0))}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(payRows.reduce((a, r) => a + r.pay.pf, 0))}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(payRows.reduce((a, r) => a + r.pay.esic, 0))}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(payRows.reduce((a, r) => a + r.pay.professionalTax, 0))}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: NAVY }}>{inr(payRows.reduce((a, r) => a + r.pay.netPay, 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Post-generation report ──────────────────────────────────────────── */}
      {report && (
        <div style={{ ...card, borderColor: 'var(--color-success-border)' }}>
          <p style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--color-success-strong)', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <CheckCircle2 size={17} aria-hidden="true" /> Saved attendance for {report.attendance} employees — {fmtMonth(report.month)}
          </p>
          <ul style={{ fontSize: '0.82rem', color: 'var(--color-ink-secondary)', lineHeight: 1.7, margin: 0, paddingLeft: 18 }}>
            <li>
              Payslips — every count verified against the database: {report.created} created, {report.updated} updated,
              {' '}{report.unchanged} already identical (not rewritten), {report.deleted.length} deleted
              {report.deleted.length ? ` (${report.deleted.join('; ')})` : ''}. Figures are stored on each slip and will not change if a salary is later revised.
            </li>
            {report.deletedPdfNote && <li style={{ color: 'var(--color-warning-strong)' }}>{report.deletedPdfNote}</li>}
            {report.designationsFilled.length > 0 && (
              <li>
                Designation filled in on {report.designationsFilled.length} employee record{report.designationsFilled.length === 1 ? '' : 's'} from the muster:
                {' '}{report.designationsFilled.join('; ')}.
              </li>
            )}
            {report.designationNote && <li style={{ color: 'var(--color-warning-strong)' }}>{report.designationNote}</li>}
            <li>
              Blocked — attendance and calculated pay saved (net {inr(report.blockedNet)} included in the month total), NO payslip:
              {' '}{report.blocked.length}{report.blocked.length ? ` — ${report.blocked.join('; ')}` : ''}.
            </li>
            {report.blockedNoPay.length > 0 && (
              <li style={{ color: 'var(--color-warning-strong)' }}>
                Blocked with NO calculable pay — MISSING from the month total until the employee record is fixed: {report.blockedNoPay.join('; ')}.
              </li>
            )}
            <li>
              Cash — attendance and calculated pay saved (net {inr(report.cashNet)} included in the month total), NO payslip by design:
              {' '}{report.cash.length}{report.cash.length ? ` — ${report.cash.join('; ')}` : ''}.
            </li>
            <li>Employees with warnings (pay unchanged): {report.warnings}.</li>
            {report.sentChanged.length > 0 && (
              <li style={{ color: 'var(--color-warning-strong)' }}>
                SENT slips whose figures changed (approved in the write plan): {report.sentChanged.join('; ')} — the employees hold
                the old version. Regenerate their PDFs and re-send from the Review tab.
              </li>
            )}
            {report.sentDeleted.length > 0 && (
              <li style={{ color: 'var(--color-warning-strong)' }}>
                SENT slips deleted (approved in the write plan): {report.sentDeleted.join('; ')} — the employees keep what was sent.
              </li>
            )}
          </ul>
          <button type="button" className="btn btn-navy btn-sm" style={{ marginTop: 12 }} onClick={() => onReviewMonth(report.month)}>
            Review {fmtMonth(report.month)}
          </button>
        </div>
      )}
    </div>
  );
}
